import { FfsResultType } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { GeckoMeasurementJson } from 'types'

import { DeratePressure, pressureToJson } from './ffsPressure'
import { countsOf, ScopeCounts, ScopeRollup } from './ffsResultRollup'
import { describeScenario, ScenarioKey } from './ffsScenarioKey'
import { ComponentRow } from './ffsUnitAssembly'
import { FfsUnitData } from './ffsUnitData'

/**
 * The final FFS result as it is written onto the ticket at sign-off.
 *
 * Ticket-root, not component-scoped: this is a unit-level fact. Modelled as a
 * discriminated union because the earlier optional-field shape permitted records
 * that cannot exist — a `pass` carrying `notApplicable`, or a `derate` with both
 * a pressure and the "unavailable" flag.
 */

export const FFS_FINAL_RESULT_FIELD = 'ffs_final_result'

export type RecordedDerate =
  | { kind: 'known'; mawp: GeckoMeasurementJson }
  | { kind: 'unavailable' }

/**
 * The recorded state of one component in a signed unit. String-valued and
 * persisted verbatim on the ticket, so the member VALUES are a wire contract —
 * they must not change even though the names may.
 */
export enum ComponentRecordState {
  NoLtasFound = 'no-ltas-found',
  NoVerdict = 'no-verdict',
  Verdict = 'verdict',
}

/** Fields every component record carries, whatever its state. */
type ComponentRecordBase = {
  componentId: string
  /** Pins the scope that was signed: the unit's component list is LIVE. */
  inspectionId: string
  componentName: string
  ltaSetId: string | null
  counts: ScopeCounts
}

/** No LTA set, or a set that was found but wholly skipped: no verdict either way. */
type UnverdictedComponent = ComponentRecordBase & {
  state: ComponentRecordState.NoLtasFound | ComponentRecordState.NoVerdict
}

/** A pass or fail verdict, backed by the reports that governed it. */
type PassFailComponent = ComponentRecordBase & {
  state: ComponentRecordState.Verdict
  result: FfsResultType.Pass | FfsResultType.Fail
  governingIds: string[]
}

/** A derate verdict: same as pass/fail, plus the re-rate pressure. */
type DerateComponent = ComponentRecordBase & {
  state: ComponentRecordState.Verdict
  result: FfsResultType.Derate
  governingIds: string[]
  derate: RecordedDerate
}

export type ComponentRecord =
  | UnverdictedComponent
  | PassFailComponent
  | DerateComponent

/** Fields the unit-level result carries whatever the outcome. */
type FfsFinalResultBase = {
  unitId: string
  /**
   * Derived from the signed scenario whenever a report governed the outcome —
   * including an all-skipped `not-applicable` unit, whose skips came from
   * reports in that scenario. Both null only when no report-backed scenario
   * governed: a Type-C-only unit, or `not-applicable` reached purely through
   * no-ltas-found / no in-scope components.
   */
  scenario: { targetDate: string | null; label: string | null }
  decidedBy: string
  decidedAt: string
  components: ComponentRecord[]
  excludedComponents: Array<{ componentId: string; reason: 'no-inspection' }>
}

type PassFailResult = FfsFinalResultBase & {
  result: FfsResultType.Pass | FfsResultType.Fail
}

type DerateResult = FfsFinalResultBase & {
  result: FfsResultType.Derate
  derate: RecordedDerate
}

/**
 * `not-applicable` stays a string literal rather than an enum member: it is a
 * result-level discriminant, not one of the component states, and
 * `FfsResultType` has no member for it.
 */
type NotApplicableResult = FfsFinalResultBase & {
  result: 'not-applicable'
  /** Counts, not one reason — a unit can reach this by mixed causes. */
  notApplicable: {
    noLtasFoundCount: number
    allSkippedCount: number
    /** 0 => the unit had no in-scope components at all. */
    inScopeComponentCount: number
  }
}

export type FfsFinalResult = PassFailResult | DerateResult | NotApplicableResult

/** An unreadable pressure stays unavailable rather than becoming a number. */
const recordDerate = (derate: DeratePressure | null): RecordedDerate =>
  derate?.kind === 'known'
    ? { kind: 'known', mawp: pressureToJson(derate.mawp) }
    : { kind: 'unavailable' }

function componentRecord(row: ComponentRow): ComponentRecord | null {
  const base = {
    componentId: row.componentId,
    inspectionId: row.inspectionId,
    componentName: row.componentName,
    ltaSetId: row.ltaSetId,
    counts: countsOf(row.rollup),
  }

  switch (row.rollup.kind) {
    case 'no-ltas-found':
      return { ...base, state: ComponentRecordState.NoLtasFound }
    case 'no-verdict':
      return { ...base, state: ComponentRecordState.NoVerdict }
    case 'verdict': {
      const { result, governingIds, derate } = row.rollup
      if (result === FfsResultType.Derate) {
        return {
          ...base,
          state: ComponentRecordState.Verdict,
          result,
          governingIds,
          derate: recordDerate(derate),
        }
      }
      return {
        ...base,
        state: ComponentRecordState.Verdict,
        result,
        governingIds,
      }
    }
    // Blocking states cannot be recorded; the caller refuses before this point.
    default:
      return null
  }
}

export interface BuildFinalResultArgs {
  unitId: string
  scenarioKey: ScenarioKey | null
  decidedBy: string
  decidedAt: string
  unitRollup: ScopeRollup
  componentRows: ReadonlyArray<ComponentRow>
  excludedComponents: FfsUnitData['excludedComponents']
}

/**
 * Build the payload, or `null` when the unit is not in a recordable state.
 *
 * Returning `null` rather than throwing keeps the "can I record?" question in
 * one place: the caller already gates on `canRecordResult`, and this is the
 * second, structural check that the two agree.
 */
export function buildFinalResult({
  unitId,
  scenarioKey,
  decidedBy,
  decidedAt,
  unitRollup,
  componentRows,
  excludedComponents,
}: BuildFinalResultArgs): FfsFinalResult | null {
  if (unitRollup.kind !== 'verdict' && unitRollup.kind !== 'no-verdict') {
    return null
  }

  const components: ComponentRecord[] = []
  for (const row of componentRows) {
    const record = componentRecord(row)
    // A blocking component means the unit rollup should not have been a verdict.
    if (!record) return null
    components.push(record)
  }

  // The scenario is only meaningful if a report actually governed something. A
  // Type-C-only unit reaches a verdict with no report at all.
  const reportGoverned = componentRows.some((row) =>
    row.ltas.some(
      (lta) =>
        (lta.disposition.kind === 'verdict' &&
          lta.disposition.report !== null) ||
        lta.disposition.kind === 'skipped'
    )
  )

  const common = {
    unitId,
    scenario:
      reportGoverned && scenarioKey
        ? describeScenario(scenarioKey)
        : { targetDate: null, label: null },
    decidedBy,
    decidedAt,
    components,
    excludedComponents: excludedComponents.map(({ componentId, reason }) => ({
      componentId,
      reason,
    })),
  }

  if (unitRollup.kind === 'no-verdict') {
    return {
      ...common,
      result: 'not-applicable',
      notApplicable: {
        noLtasFoundCount: components.filter(
          (c) => c.state === ComponentRecordState.NoLtasFound
        ).length,
        allSkippedCount: components.filter(
          (c) => c.state === ComponentRecordState.NoVerdict
        ).length,
        inScopeComponentCount: components.length,
      },
    }
  }

  if (unitRollup.result === FfsResultType.Derate) {
    return {
      ...common,
      result: FfsResultType.Derate,
      derate: recordDerate(unitRollup.derate),
    }
  }

  return { ...common, result: unitRollup.result }
}
